/* eslint-disable max-lines-per-function */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from 'react';

import { useAuth } from '@/hooks/useAuth';
import { createSupabaseClient } from '@/integrations/supabase/client';
import { AVAILABLE_TOOLS, Tool } from '@/types/tools';

export interface ToolItem {
  id: string;
  name: string;
  versionsCount: number;
  versions: ToolVersionItem[];
  updatedAt: string | null;
}

export interface ToolVersionItem {
  id: string;
  name: string;
  editedAt: string | null;
}

type ToolVersionRow = {
  id: string;
  version_name: string | null;
  tool_id: string;
  updated_at: string | null;
};

export const useTools = () => {
  const [tools, setTools] = useState<ToolItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, getToken } = useAuth();

  const supabase = useMemo(() => createSupabaseClient(getToken), [getToken]);

  const toolNameById = useMemo<Map<string, string>>(
    () => new Map(AVAILABLE_TOOLS.map((tool: Tool) => [tool.id, tool.name])),
    []
  );

  const fetchTools = async () => {
    if (!user) {
      return;
    }

    setLoading(true);
    try {
      const { data } = await supabase
        .from('tool_versions')
        .select('id, version_name, tool_id, updated_at')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });

      const toolVersions = (data ?? []) as ToolVersionRow[];
      const uniqueTools = toolVersions.filter(
        (item: ToolVersionRow, index: number, items: ToolVersionRow[]) =>
          items.findIndex(
            (tool: ToolVersionRow) => tool.tool_id === item.tool_id
          ) === index
      );

      const cleanedData = uniqueTools.map((item: ToolVersionRow) => {
        const name: string =
          toolNameById.get(item.tool_id) ?? item.version_name ?? item.tool_id;
        const versions = toolVersions.filter(
          (tool: ToolVersionRow) => tool.tool_id === item.tool_id
        );

        const cleanedVersions = versions.map((item: ToolVersionRow) => {
          const name: string =
            item.version_name ?? toolNameById.get(item.tool_id) ?? item.tool_id;

          return {
            id: item.id,
            name,
            editedAt: item.updated_at,
          };
        });

        return {
          id: item.id,
          name,
          versionsCount: versions.length,
          versions: cleanedVersions,
          updatedAt: item.updated_at,
        };
      });

      setTools(cleanedData);
    } catch (error) {
      console.error('Error fetching tools from user:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTools();
  }, [user, supabase]);

  return {
    tools,
    loading,
  };
};
